/**
 * Pure helpers behind RunSummaryCards (M2 contract 9, F5): result verdict, detection funnel,
 * per-stage durations and the delta against the previous SUCCEEDED run. Inputs are the
 * `metadata.run` block and the gestures payload of the analysisData store.
 */
import { blockScoreForCoherenceMode } from '@/utils/coherenceSemanticsTimeline';

export const DEFAULT_IDEA_THRESHOLD_PCT = 50;

function ideaBlocksOf(gestures) {
  const modes = gestures?.coherence_analysis?.semantic_modes;
  const mode = modes?.diarization || modes?.punctuation;
  if (!mode) return null;
  if (Array.isArray(mode.blocks_idea)) return mode.blocks_idea;
  if (Array.isArray(mode.blocks)) {
    return mode.blocks.filter((block) => block?.block_kind === 'idea');
  }
  return null;
}

/** Idea coincidence threshold sealed in the run's config snapshot (default 50). */
export function ideaThresholdPct(gestures) {
  const coherence = gestures?.analysis_config_snapshot?.engines?.coherence || {};
  const candidates = [coherence.ideaCoincidenceThresholdPct, coherence.detectionThresholdPct];
  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0 && n <= 100) return n;
  }
  return DEFAULT_IDEA_THRESHOLD_PCT;
}

/**
 * Detection funnel of one run: raw episodes -> surviving alerts -> ideas -> ideas subtracting
 * (score under `scoreMode` strictly below the threshold).
 * @returns {{ episodes: number, alerts: number, ideas: number|null, subtracting: number|null,
 *   threshold_pct: number, analysis_available: boolean }}
 */
export function computeFunnel(gestures, { scoreMode = 'computed_subtraction' } = {}) {
  const episodes = Array.isArray(gestures?.episodes) ? gestures.episodes.length : 0;
  const alerts = Array.isArray(gestures?.alerts) ? gestures.alerts.length : 0;
  const ideaBlocks = ideaBlocksOf(gestures);
  const threshold = ideaThresholdPct(gestures);
  if (!ideaBlocks) {
    return {
      episodes,
      alerts,
      ideas: null,
      subtracting: null,
      threshold_pct: threshold,
      analysis_available: false,
    };
  }
  const subtracting = ideaBlocks.filter(
    (block) => blockScoreForCoherenceMode(block, scoreMode) < threshold
  ).length;
  return {
    episodes,
    alerts,
    ideas: ideaBlocks.length,
    subtracting,
    threshold_pct: threshold,
    analysis_available: true,
  };
}

/**
 * Engine verdict of the run: 'incoherence_detected' when at least one idea subtracts, 'cleared' when
 * none does, 'no_result' when the coherence analysis is missing.
 */
export function resultVerdict(funnel) {
  if (!funnel || !funnel.analysis_available) return 'no_result';
  return funnel.subtracting > 0 ? 'incoherence_detected' : 'cleared';
}

export const RESULT_PRESENTATION = Object.freeze({
  incoherence_detected: { label: 'Lie detected', color: 'error', icon: 'mdi-alert-octagon-outline' },
  cleared: { label: 'Cleared', color: 'success', icon: 'mdi-shield-check-outline' },
  no_result: { label: 'No result', color: 'grey', icon: 'mdi-help-circle-outline' },
});

/** Human label status: 'no label', 'labeled lie' or 'labeled truthful' (video_incoherence row). */
export function humanLabelText(incoherence) {
  if (!incoherence) return 'No label';
  return incoherence.label_type === 'truthful' ? 'Labeled truthful' : 'Labeled lie';
}

function toMillis(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function formatMs(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(Number(ms))) return '—';
  const total = Math.max(0, Math.round(Number(ms)));
  if (total < 1000) return `${total} ms`;
  const seconds = Math.round(total / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

/**
 * Total wall time (finished - started) and one row per stage of the run block.
 * @returns {{ total_ms: number|null, stages: Array<{ name: string, status: string,
 *   duration_ms: number|null }> }}
 */
export function stageDurations(run) {
  const started = toMillis(run?.started_at);
  const finished = toMillis(run?.finished_at);
  const totalMs = started !== null && finished !== null && finished >= started ? finished - started : null;
  const stages = (Array.isArray(run?.stages) ? run.stages : [])
    .filter((stage) => stage && stage.name)
    .map((stage) => {
      const duration = Number(stage.duration_ms);
      const from = toMillis(stage.started_at);
      const to = toMillis(stage.finished_at);
      return {
        name: String(stage.name),
        status: String(stage.status || ''),
        duration_ms: Number.isFinite(duration)
          ? duration
          : from !== null && to !== null && to >= from
            ? to - from
            : null,
      };
    });
  return { total_ms: totalMs, stages };
}

/**
 * Client-side delta of the run being viewed against the previous SUCCEEDED run.
 * @returns {{ alerts: number|null, subtracting: number|null } | null} null without previous
 */
export function deltaVsPrevious(currentFunnel, previousFunnel) {
  if (!currentFunnel || !previousFunnel) return null;
  const delta = (a, b) =>
    a === null || a === undefined || b === null || b === undefined ? null : a - b;
  return {
    episodes: delta(currentFunnel.episodes, previousFunnel.episodes),
    alerts: delta(currentFunnel.alerts, previousFunnel.alerts),
    subtracting: delta(currentFunnel.subtracting, previousFunnel.subtracting),
  };
}

export function formatDelta(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  if (n === 0) return '±0';
  return n > 0 ? `+${n}` : `${n}`;
}

/**
 * Previous SUCCEEDED run of the same source: the SUCCEEDED run with the highest attempt below
 * the current one (falls back to the most recent SUCCEEDED run other than the current).
 */
export function previousSucceededRun(runs, currentRun) {
  const list = (Array.isArray(runs) ? runs : []).filter(
    (run) => run && String(run.status).toUpperCase() === 'SUCCEEDED'
  );
  const currentId = currentRun ? String(currentRun.run_id ?? currentRun._id ?? '') : '';
  const currentAttempt = Number(currentRun?.attempt);
  const candidates = list.filter((run) => String(run._id ?? run.run_id) !== currentId);
  const earlier = Number.isFinite(currentAttempt)
    ? candidates.filter((run) => Number(run.attempt) < currentAttempt)
    : candidates;
  const pool = earlier.length ? earlier : candidates;
  if (!pool.length) return null;
  return [...pool].sort((a, b) => (Number(b.attempt) || 0) - (Number(a.attempt) || 0))[0];
}
